import { z } from 'zod';
import { amountCents, currency, isoDate, uuid } from './common.js';

export const eventStatus = z.enum(['draft', 'open', 'closed', 'archived']);
/** `mixed`: a format across disciplines, e.g. the gravel + trail relay (P-20). */
export const discipline = z.enum(['gravel', 'running', 'mixed']);
/**
 * `team`: a captain names a team and pays their own place; teammates pay theirs from an
 * invitation link, at the captain's tier (PRD §4.7, record 0021).
 */
export const registrationMode = z.enum(['individual', 'team']);

/** Smallest team the product allows (PRD §4.7). */
export const TEAM_SIZE_MIN = 2;
export type RegistrationMode = z.infer<typeof registrationMode>;

export const pricingTier = z.object({
  id: uuid,
  formatId: uuid,
  name: z.string(),
  priceCents: amountCents,
  startsAt: isoDate.nullable(),
  endsAt: isoDate.nullable(),
  capacity: z.number().int().positive().nullable(),
});
export type PricingTier = z.infer<typeof pricingTier>;

export const format = z.object({
  id: uuid,
  eventId: uuid,
  slug: z.string(),
  /** French name and description; `nameEn` / `descriptionEn` null = show the French ones. */
  name: z.string(),
  nameEn: z.string().nullable(),
  description: z.string().nullable(),
  descriptionEn: z.string().nullable(),
  discipline,
  distanceKm: z.number().positive().nullable(),
  elevationM: z.number().int().nonnegative().nullable(),
  startsAt: isoDate.nullable(),
  /** Persons, for every format: each team member is a paying registration. */
  capacity: z.number().int().positive().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  /**
   * Team format (P-20): `teamSizeMin` ≥ 2, `teamSizeMax` null = unlimited, equal to the
   * minimum = fixed size. Both null for individual formats.
   */
  registrationMode,
  teamSizeMin: z.number().int().min(TEAM_SIZE_MIN).nullable(),
  teamSizeMax: z.number().int().min(TEAM_SIZE_MIN).nullable(),
  /** Medical certificate or licence to provide after registration (running formats). */
  requiresMedicalDocument: z.boolean(),
  currentTier: pricingTier.nullable(),
  /**
   * Price of the format's last tier (the standard price). Shown struck through next to the
   * current tier when it is lower (early bird). Null when the format has no tier.
   */
  standardPriceCents: amountCents.nullable(),
  /**
   * With no tier on sale: when the next tier opens ("Ouverture le …"). Null with no tier on
   * sale means registration is closed. Always null while a tier is on sale.
   */
  opensAt: isoDate.nullable(),
  /** Price of the tier opening at `opensAt`. Null whenever `opensAt` is null. */
  nextPriceCents: amountCents.nullable(),
  remainingSpots: z.number().int().nullable(),
});
export type Format = z.infer<typeof format>;

/**
 * Beticketing cancellation insurance offered on the email step (P-19, decision 0025). Null on
 * the event when it is not offered: no rate set, the insurer isn't configured, or the event
 * has started (the insurer's risk closes at the start). The premium is `rateBps` of the
 * formats after the voucher (`insurancePremiumCents`); the server recomputes it at checkout.
 */
export const insuranceOffer = z.object({
  /** Premium rate in basis points of the insured amount: 1700 = 17 %. */
  rateBps: z.number().int().positive(),
  /** The insurer's information notice (contractual documents). EN null = the FR one. */
  termsUrlFr: z.string().url(),
  termsUrlEn: z.string().url().nullable(),
});
export type InsuranceOffer = z.infer<typeof insuranceOffer>;

export const publicEvent = z.object({
  id: uuid,
  slug: z.string(),
  name: z.string(),
  edition: z.string(),
  status: eventStatus,
  startsAt: isoDate.nullable(),
  endsAt: isoDate.nullable(),
  location: z.string().nullable(),
  currency,
  /** The sporting rules: a page of the site (`/reglement`) or an https link. */
  rulesUrl: z.string().nullable(),
  insurance: insuranceOffer.nullable(),
  formats: z.array(format),
});
export type PublicEvent = z.infer<typeof publicEvent>;
